import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { BreadCrumb } from 'primereact/breadcrumb';
import { Button } from 'primereact/button';
import { AppTimezoneCalendar as Calendar } from '@/components/timezone-calendar/AppTimezoneCalendar';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { AppDataTable as DataTable } from '@/components/loading';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import { ReportExportToolbar } from '@/components/report-export-toolbar';
import { useHasAnyAuthority } from '@/shared/auth/use-has-any-authority';
import { APP_FEATURE_GUARDS } from '@/shared/constants/app-permissions';
import { useAppDispatch, useAppSelector } from '@/store';
import { clearErrorMessage, fetchAuditLogRows, getAuditLogs } from '@/shared/reducers/auditLog.reducer';
import { useUsersCatalog } from '@/shared/hooks/useEntityCatalog';
import { exportReportCsv } from '@/shared/utils/export-csv';
import { formatTableDateTime } from '@/shared/utils/app-timezone';
import { defaultFilterDateRange } from '@/shared/utils/date-range';
import { useReducerErrorToast } from '@/shared/hooks/useReducerErrorToast';
const toIsoParam = (value) => {
    if (!value) {
        return undefined;
    }
    return value.toISOString();
};
const formatDateTime = (value) => formatTableDateTime(value, '');
const auditTimestamp = (row) => row.createdDate ?? row.eventDate ?? '';
const auditActivityLabel = (row) => row.activity ?? row.eventType ?? row.action ?? row.entityName ?? row.entityType ?? '';
const auditActivityDetail = (row) => row.activityDetail ?? row.details ?? row.reason ?? '';
function exportAuditLogsToCsv(rows) {
    exportReportCsv({
        filename: 'Audit_Log',
        title: 'Audit Log',
        headers: [
            '#',
            'Username',
            'Activity',
            'Transaction Name',
            'Entity Name',
            'Activity Details',
            'Created Date',
        ],
        rows: rows.map((row, index) => [
            index + 1,
            row.username ?? '',
            auditActivityLabel(row),
            row.transactionName ?? '',
            row.entityName ?? row.entityType ?? '',
            auditActivityDetail(row),
            formatDateTime(auditTimestamp(row)),
        ]),
    });
}
const AuditLogPage = () => {
    const dispatch = useAppDispatch();
    const canReadAuditLog = useHasAnyAuthority(APP_FEATURE_GUARDS.auditLogRead);
    const { logs, loading, totalItems, errorMessage } = useAppSelector((state) => state.auditLog);
    const { users, loading: usersLoading } = useUsersCatalog();
    const initialDateRange = defaultFilterDateRange();
    const [usernameFilter, setUsernameFilter] = useState(null);
    const [activityFilter, setActivityFilter] = useState('');
    const [searchTextFilter, setSearchTextFilter] = useState('');
    const [dateFromFilter, setDateFromFilter] = useState(initialDateRange.startDate);
    const [dateToFilter, setDateToFilter] = useState(initialDateRange.endDate);
    const [appliedUsername, setAppliedUsername] = useState();
    const [appliedActivity, setAppliedActivity] = useState();
    const [appliedSearchText, setAppliedSearchText] = useState();
    const [appliedStartDate, setAppliedStartDate] = useState(toIsoParam(initialDateRange.startDate));
    const [appliedEndDate, setAppliedEndDate] = useState(toIsoParam(initialDateRange.endDate));
    const [entityFirst, setEntityFirst] = useState(0);
    const [entityPage, setEntityPage] = useState(0);
    const [entitySize, setEntitySize] = useState(20);
    const entitySort = 'createdDate,desc';
    useReducerErrorToast(errorMessage, {
        summary: 'Audit Log',
        onShown: () => dispatch(clearErrorMessage()),
    });
    const userOptions = useMemo(() => users
        .map((user) => {
        const value = user.username?.trim() || user.login?.trim() || '';
        if (!value) {
            return null;
        }
        const name = user.fullName?.trim();
        return {
            label: name ? `${value} — ${name}` : value,
            value,
        };
    })
        .filter((option) => option != null), [users]);
    const fetchLogs = (page, size, filters) => {
        void dispatch(getAuditLogs({
            page,
            size,
            sort: entitySort,
            username: filters?.username,
            activity: filters?.activity,
            startDate: filters?.startDate,
            endDate: filters?.endDate,
            searchText: filters?.searchText,
        }));
    };
    useEffect(() => {
        if (!canReadAuditLog) {
            return;
        }
        fetchLogs(entityPage, entitySize, {
            username: appliedUsername,
            activity: appliedActivity,
            startDate: appliedStartDate,
            endDate: appliedEndDate,
            searchText: appliedSearchText,
        });
    }, [
        canReadAuditLog,
        dispatch,
        entityPage,
        entitySize,
        appliedUsername,
        appliedActivity,
        appliedStartDate,
        appliedEndDate,
        appliedSearchText,
    ]);
    const onSearch = () => {
        if (!canReadAuditLog) {
            return;
        }
        if (dateFromFilter && dateToFilter && dateFromFilter > dateToFilter) {
            return;
        }
        setAppliedUsername(usernameFilter?.trim() || undefined);
        setAppliedActivity(activityFilter.trim() || undefined);
        setAppliedSearchText(searchTextFilter.trim() || undefined);
        setAppliedStartDate(toIsoParam(dateFromFilter));
        setAppliedEndDate(toIsoParam(dateToFilter));
        setEntityPage(0);
        setEntityFirst(0);
    };
    const onReset = () => {
        const { startDate, endDate } = defaultFilterDateRange();
        setUsernameFilter(null);
        setActivityFilter('');
        setSearchTextFilter('');
        setDateFromFilter(startDate);
        setDateToFilter(endDate);
        setAppliedUsername(undefined);
        setAppliedActivity(undefined);
        setAppliedSearchText(undefined);
        setAppliedStartDate(toIsoParam(startDate));
        setAppliedEndDate(toIsoParam(endDate));
        setEntityPage(0);
        setEntityFirst(0);
    };
    const onPage = (event) => {
        const nextPage = event.page ?? 0;
        const nextSize = event.rows ?? entitySize;
        setEntityFirst(event.first ?? 0);
        setEntityPage(nextPage);
        setEntitySize(nextSize);
    };
    const onExport = async () => {
        if (!canReadAuditLog) {
            return;
        }
        const exportSize = Math.max(totalItems, logs.length, 1);
        try {
            const { rows } = await fetchAuditLogRows({
                page: 0,
                size: exportSize,
                sort: entitySort,
                username: appliedUsername,
                activity: appliedActivity,
                startDate: appliedStartDate,
                endDate: appliedEndDate,
                searchText: appliedSearchText,
            });
            exportAuditLogsToCsv(rows);
        }
        catch {
            exportAuditLogsToCsv(logs);
        }
    };
    const rowIndexTemplate = (_rowData, options) => (_jsx("span", { children: entityFirst + options.rowIndex + 1 }));
    const filterHeader = (_jsxs("div", { className: "flex align-items-center gap-2", children: [_jsx("i", { className: "pi pi-filter text-primary" }), _jsx("span", { className: "font-semibold", children: "Search and Filters" })] }));
    const footerTemplate = (_jsxs("div", { className: "flex gap-2 justify-content-end", children: [_jsx(Button, { label: "Search", icon: "pi pi-search", onClick: onSearch, disabled: !canReadAuditLog }), _jsx(Button, { label: "Reset", icon: "pi pi-times", severity: "secondary", onClick: onReset, disabled: !canReadAuditLog })] }));
    if (!canReadAuditLog) {
        return (_jsxs(_Fragment, { children: [_jsx(BreadCrumb, { model: [{ label: 'Settings' }, { label: 'Audit Log' }], home: { icon: 'pi pi-home', url: '/' }, className: "mb-2" }), _jsx(Message, { severity: "warn", text: "You do not have permission to view the audit log.", className: "w-full" })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(BreadCrumb, { model: [{ label: 'Settings' }, { label: 'Audit Log' }], home: { icon: 'pi pi-home', url: '/' }, className: "mb-2" }), _jsxs(Card, { title: "Audit Log", children: [_jsx(Panel, { header: filterHeader, footer: footerTemplate, children: _jsxs("div", { className: "grid", children: [_jsxs("div", { className: "col-12 md:col-4", children: [_jsx("label", { htmlFor: "auditUsernameFilter", className: "block font-medium mb-2", children: "Username" }), _jsx(Dropdown, { id: "auditUsernameFilter", value: usernameFilter, options: userOptions, onChange: (e) => setUsernameFilter(e.value ?? null), optionLabel: "label", optionValue: "value", placeholder: "All users", filter: true, showClear: true, className: "w-full", loading: usersLoading })] }), _jsxs("div", { className: "col-12 md:col-4", children: [_jsx("label", { htmlFor: "auditActivityFilter", className: "block font-medium mb-2", children: "Activity" }), _jsx(InputText, { id: "auditActivityFilter", value: activityFilter, onChange: (e) => setActivityFilter(e.target.value), placeholder: "e.g. LOGOUT", className: "w-full" })] }), _jsxs("div", { className: "col-12 md:col-4", children: [_jsx("label", { htmlFor: "auditSearchTextFilter", className: "block font-medium mb-2", children: "Search Text" }), _jsx(InputText, { id: "auditSearchTextFilter", value: searchTextFilter, onChange: (e) => setSearchTextFilter(e.target.value), placeholder: "Search message or details\u2026", className: "w-full" })] }), _jsxs("div", { className: "col-12 md:col-6", children: [_jsx("label", { htmlFor: "auditDateFromFilter", className: "block font-medium mb-2", children: "Start Date" }), _jsx(Calendar, { id: "auditDateFromFilter", value: dateFromFilter, onChange: (e) => setDateFromFilter(e.value), showTime: true, hourFormat: "24", dateFormat: "yy-mm-dd", showButtonBar: true, className: "w-full", inputClassName: "w-full" })] }), _jsxs("div", { className: "col-12 md:col-6", children: [_jsx("label", { htmlFor: "auditDateToFilter", className: "block font-medium mb-2", children: "End Date" }), _jsx(Calendar, { id: "auditDateToFilter", value: dateToFilter, onChange: (e) => setDateToFilter(e.value), showTime: true, hourFormat: "24", dateFormat: "yy-mm-dd", showButtonBar: true, className: "w-full", inputClassName: "w-full" })] })] }) }), _jsx(ReportExportToolbar, { recordCount: totalItems, title: "Audit records", subtitle: "Download all rows from the current search as a CSV spreadsheet", exportLabel: "Export CSV", loading: loading, onExport: () => void onExport() }), _jsxs(DataTable, { className: "mt-0", value: [...logs], loading: loading, stripedRows: true, scrollable: true, scrollHeight: "60vh", emptyMessage: "No audit records found", lazy: true, paginator: true, first: entityFirst, rows: entitySize, totalRecords: totalItems, rowsPerPageOptions: [10, 20, 50, 100], paginatorPosition: "bottom", onPage: onPage, children: [_jsx(Column, { header: "#", body: rowIndexTemplate, style: { width: '5rem', textAlign: 'center' } }), _jsx(Column, { field: "username", header: "Username", style: { minWidth: '10rem' } }), _jsx(Column, { header: "Activity", body: (row) => auditActivityLabel(row), style: { minWidth: '10rem' } }), _jsx(Column, { field: "transactionName", header: "Transaction Name", style: { minWidth: '11rem' } }), _jsx(Column, { header: "Entity Name", body: (row) => row.entityName ?? row.entityType ?? '', style: { minWidth: '9rem' } }), _jsx(Column, { header: "Created Date", body: (row) => formatDateTime(auditTimestamp(row)), style: { minWidth: '12rem' } })] })] })] }));
};
export default AuditLogPage;
